import React from 'react';
import type { WidgetSize } from '../../types/dashboard';
import { fmtMoney } from '../../utils/formatters';
import type { BucketAnalyticsResult, BucketItem } from '../../lib/trades/dashboardBucketAnalytics';

interface BucketPerformanceListProps {
  result: BucketAnalyticsResult;
  size: WidgetSize;
  labelWidthClass?: string;
  positiveValueClass?: string;
  positiveBarClass?: string;
  negativeValueClass?: string;
  negativeBarClass?: string;
  unavailableMessage?: string;
  emptyTitle?: string;
  emptyMessage?: string;
  showOverlay?: boolean;
  pctFormatter?: (item: BucketItem) => string;
}

export const BucketPerformanceList: React.FC<BucketPerformanceListProps> = ({
  result,
  size,
  labelWidthClass = 'w-32',
  positiveValueClass = 'text-emerald-500',
  positiveBarClass = 'bg-emerald-500',
  negativeValueClass = 'text-red-500',
  negativeBarClass = 'bg-red-500',
  unavailableMessage = 'N/A',
  emptyTitle = 'No performance data yet.',
  emptyMessage = 'Import trades to see this widget.',
  showOverlay = true,
  pctFormatter = (item) => `${item.pct.toFixed(1)}%`,
}) => {
  if (result.state === 'empty') {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <div>
          <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{emptyTitle}</div>
          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{emptyMessage}</div>
        </div>
      </div>
    );
  }

  if (result.state === 'na') {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-500 dark:text-zinc-400">
        {unavailableMessage}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto pr-2">
        <div className={`space-y-2.5 ${size === 'mini' ? 'space-y-1' : 'space-y-2.5'}`}>
          {result.items.map((item) => {
            const valueClass = item.pnl > 0
              ? positiveValueClass
              : item.pnl < 0
                ? negativeValueClass
                : 'text-zinc-500 dark:text-zinc-400';
            const barClass = item.pnl >= 0 ? positiveBarClass : negativeBarClass;
            const widthPct = (Math.abs(item.pnl) / result.maxPnl) * 100;

            return (
              <div key={item.name} className="border-b border-zinc-200 dark:border-zinc-800 pb-2 last:border-b-0">
                <div className={`flex justify-between items-center ${size === 'mini' ? 'text-xs' : 'text-sm'}`}>
                  <span className={`font-semibold ${labelWidthClass}`}>{item.name}</span>
                  <div className="flex items-center gap-4">
                    <span className={`font-semibold w-28 text-right ${valueClass}`}>
                      {fmtMoney(item.pnl)}
                    </span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400 w-12 text-right">
                      {pctFormatter(item)}
                    </span>
                  </div>
                </div>
                {item.pnl !== 0 && (
                  <div className="w-full bg-zinc-200 dark:bg-zinc-700/50 rounded-full h-1.5 mt-1 relative group cursor-pointer overflow-hidden">
                    <div
                      data-testid={`bucket-performance-bar-${item.name}`}
                      className={`${barClass} h-1.5 rounded-full`}
                      style={{ width: `${widthPct}%` }}
                    />
                    {showOverlay && (
                      <div
                        className="absolute inset-y-0 left-0 h-1.5 rounded-full bg-zinc-400/25 opacity-0 transition group-hover:opacity-100 pointer-events-none"
                        style={{ width: `${widthPct}%` }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
